import { useEffect, useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Typography,
  TextField,
  Alert,
  Link,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useLocale } from "../contexts/LocaleContext";
import LanguageSelector from "../components/LanguageSelector";

function authErrorKey(code) {
  const map = {
    "auth/invalid-email": "invalidEmail",
    "auth/user-disabled": "userDisabled",
    "auth/user-not-found": "userNotFound",
  };
  return map[code] || "default";
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ForgotPassword() {
  const { user, loading, sendPasswordReset } = useAuth();
  const { t } = useTranslation();
  const { basePath, localizePath } = useLocale();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState(null);

  useEffect(() => {
    if (!loading && user) {
      navigate(basePath || "/", { replace: true });
    }
  }, [user, loading, navigate, basePath]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError(t("login.forgot.errorEnterEmail"));
      return;
    }
    if (!EMAIL_RE.test(trimmedEmail)) {
      setError(t("login.forgot.errorInvalidEmail"));
      return;
    }
    setSubmitting(true);
    try {
      await sendPasswordReset(trimmedEmail);
      setSentTo(trimmedEmail);
    } catch (err) {
      // Treat user-not-found as success to avoid leaking which emails are registered.
      if (err.code === "auth/user-not-found") {
        setSentTo(trimmedEmail);
      } else {
        const key = authErrorKey(err.code);
        setError(t(`login.authErrors.${key}`) || err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "#ffffff", minHeight: "100vh", display: "flex", alignItems: "center" }}>
      <Container maxWidth="sm">
        <Box sx={{ py: 8, position: "relative" }}>
          <Box sx={{ position: "absolute", top: 0, right: 0 }}>
            <LanguageSelector />
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Cal Sans", "Inter", sans-serif',
              fontWeight: 600,
              letterSpacing: "-0.02em",
              color: "#111827",
              mb: 2,
              textAlign: "center",
            }}
          >
            RepRadar
          </Typography>

          {sentTo ? (
            <>
              <Typography
                variant="h6"
                sx={{ color: "#111827", fontWeight: 600, mb: 1, textAlign: "center" }}
              >
                {t("login.forgot.successTitle")}
              </Typography>
              <Typography sx={{ color: "#6b7280", mb: 4, textAlign: "center" }}>
                {t("login.forgot.successBody", { email: sentTo })}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Link
                  component={RouterLink}
                  to={localizePath("/login")}
                  sx={{ color: "#111827", fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                >
                  {t("login.forgot.backToSignIn")}
                </Link>
              </Box>
            </>
          ) : (
            <>
              <Typography
                variant="h6"
                sx={{ color: "#111827", fontWeight: 600, mb: 1, textAlign: "center" }}
              >
                {t("login.forgot.title")}
              </Typography>
              <Typography sx={{ color: "#6b7280", mb: 4, textAlign: "center" }}>
                {t("login.forgot.subtitle")}
              </Typography>

              <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {error && (
                  <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}
                <TextField
                  label={t("login.email")}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  fullWidth
                  required
                  autoFocus
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  sx={{
                    bgcolor: "#111827",
                    textTransform: "none",
                    py: 1.5,
                    "&:hover": { bgcolor: "#374151" },
                  }}
                >
                  {submitting ? t("login.forgot.sending") : t("login.forgot.sendLink")}
                </Button>
                <Typography sx={{ textAlign: "center", color: "#6b7280", fontSize: 14 }}>
                  <Link
                    component={RouterLink}
                    to={localizePath("/login")}
                    sx={{ color: "#111827", fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                  >
                    {t("login.forgot.backToSignIn")}
                  </Link>
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
}
